import React from "react";
import {
  Building2,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  FileWarning,
  Banknote,
  AlertOctagon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useInsights } from "../../data/insightsStore";

const Card = ({ children, testid, className = "" }) => (
  <div data-testid={testid} className={`rounded-2xl border border-slate-200 bg-white cf-shadow p-5 ${className}`}>
    {children}
  </div>
);

const SectionHead = ({ eyebrow, title, icon: Icon }) => (
  <div className="flex items-center gap-2 mb-3">
    {Icon && (
      <div className="w-8 h-8 rounded-lg bg-slate-900 text-indigo-300 flex items-center justify-center">
        <Icon size={14} />
      </div>
    )}
    <div>
      <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{eyebrow}</p>
      <h3 className="font-display text-[15.5px] font-bold text-slate-900 leading-tight">{title}</h3>
    </div>
  </div>
);

const MomentumIcon = ({ pct }) => {
  if (pct >= 15) return <TrendingUp size={14} className="text-indigo-600" />;
  if (pct <= -10) return <TrendingDown size={14} className="text-rose-600" />;
  return <Minus size={14} className="text-slate-500" />;
};

export const DirectorPanel = () => {
  const navigate = useNavigate();
  const { data } = useInsights();
  const dir = data?.director;
  if (!dir) return null;

  return (
    <section data-testid="director-panel" className="space-y-4">
      {/* Director overview band — 4 stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="director-overview">
        <Card testid="dir-pipeline-value">
          <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Pipeline value</p>
          <p className="font-display text-[26px] font-bold text-slate-900 mt-2 leading-none">{dir.pipeline_value}</p>
          <p className="text-[11px] text-slate-500 mt-1.5">
            <span className="text-rose-600 font-semibold">{dir.pipeline_value_at_risk}</span> at risk
          </p>
        </Card>
        <Card testid="dir-exchange-prob">
          <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Avg exchange probability</p>
          <p className="font-display text-[26px] font-bold text-slate-900 mt-2 leading-none">{dir.avg_exchange_probability}%</p>
          <p className="text-[11px] text-slate-500 mt-1.5">Across {data.deal_count} active deal{data.deal_count === 1 ? "" : "s"}</p>
        </Card>
        <Card testid="dir-momentum">
          <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Transaction momentum</p>
          <div className="flex items-center gap-1.5 mt-2">
            <MomentumIcon pct={dir.momentum_pct} />
            <p className="font-display text-[26px] font-bold text-slate-900 leading-none">{dir.momentum_pct >= 0 ? "+" : ""}{dir.momentum_pct}%</p>
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">{dir.momentum_label} · 7d vs prior 7d</p>
        </Card>
        <Card testid="dir-days-pipeline">
          <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Avg days in pipeline</p>
          <p className="font-display text-[26px] font-bold text-slate-900 mt-2 leading-none">{dir.avg_days_in_pipeline}</p>
          <p className="text-[11px] text-slate-500 mt-1.5">From offer accepted to today</p>
        </Card>
      </div>

      {/* Three-column: bottlenecks · mortgage expiry · missing docs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Bottlenecks */}
        <Card testid="dir-bottlenecks">
          <SectionHead eyebrow="Stage bottlenecks" title="Where deals are stuck" icon={Clock} />
          {dir.bottlenecks?.length ? (
            <ul className="space-y-2.5">
              {dir.bottlenecks.map((b, i) => (
                <li key={i} data-testid={`bottleneck-${i}`} className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-slate-900 truncate">{b.stage}</p>
                    <p className="text-[10.5px] text-slate-500">No activity 5+ days</p>
                  </div>
                  <span className={`text-[10.5px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                    b.tone === "high" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"
                  }`}>
                    {b.stuck_deals} deal{b.stuck_deals === 1 ? "" : "s"}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[12px] text-slate-500">No bottlenecks — every deal has moved in the last 5 days.</p>
          )}
        </Card>

        {/* Mortgage offer expiry */}
        <Card testid="dir-mortgage-expiry">
          <SectionHead eyebrow="Mortgage offer expiry" title="Risk to exchange window" icon={Banknote} />
          {dir.mortgage_expiring_soon?.length ? (
            <ul className="space-y-2.5">
              {dir.mortgage_expiring_soon.map((m, i) => (
                <li
                  key={i}
                  data-testid={`mortgage-expiring-${i}`}
                  onClick={() => navigate(`/deal-rooms/${m.deal_id}`)}
                  className="flex items-center justify-between cursor-pointer hover:bg-slate-50/80 rounded px-1 -mx-1 py-0.5"
                >
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-slate-900 truncate">{m.address}</p>
                    <p className="text-[10.5px] text-slate-500">Mortgage offer</p>
                  </div>
                  <span className={`text-[10.5px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                    m.tone === "high" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"
                  }`}>
                    {m.days_left}d left
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[12px] text-slate-500">No mortgage offers expiring in the next 30 days.</p>
          )}
        </Card>

        {/* Missing documents */}
        <Card testid="dir-missing-docs">
          <SectionHead eyebrow="Missing documents" title="Outstanding requests" icon={FileWarning} />
          {dir.missing_documents?.length ? (
            <ul className="space-y-2.5 max-h-[260px] overflow-y-auto cf-scroll pr-1">
              {dir.missing_documents.map((d, i) => (
                <li
                  key={i}
                  data-testid={`missing-doc-${i}`}
                  onClick={() => navigate(`/deal-rooms/${d.deal_id}`)}
                  className="flex items-center justify-between cursor-pointer hover:bg-slate-50/80 rounded px-1 -mx-1 py-0.5"
                >
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-slate-900 truncate">{d.label}</p>
                    <p className="text-[10.5px] text-slate-500 truncate">{d.address} · awaiting {d.owner}</p>
                  </div>
                  <span className={`text-[10.5px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                    d.tone === "high" ? "bg-rose-50 text-rose-700 border-rose-200" :
                    d.tone === "med" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                       "bg-indigo-50 text-indigo-700 border-indigo-200"
                  }`}>
                    {d.days_open}d
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[12px] text-slate-500">No outstanding document requests.</p>
          )}
        </Card>
      </div>

      {/* Office-level pipeline visibility */}
      {dir.office_pipelines?.length > 0 && (
        <Card testid="dir-office-pipelines">
          <SectionHead eyebrow="Office pipelines" title="Director-level visibility" icon={Building2} />
          <div className="overflow-x-auto cf-scroll">
            <table className="w-full text-left min-w-[640px]">
              <thead>
                <tr className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold border-b border-slate-100">
                  <th className="px-2 py-2 font-semibold">Office</th>
                  <th className="px-2 py-2 font-semibold">Active</th>
                  <th className="px-2 py-2 font-semibold">Pipeline value</th>
                  <th className="px-2 py-2 font-semibold">Avg chain</th>
                  <th className="px-2 py-2 font-semibold">At risk</th>
                </tr>
              </thead>
              <tbody>
                {dir.office_pipelines.map((o, i) => (
                  <tr key={i} data-testid={`office-row-${i}`} className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50/60">
                    <td className="px-2 py-3">
                      <p className="text-[13px] font-semibold text-slate-900">{o.office}</p>
                    </td>
                    <td className="px-2 py-3">
                      <p className="font-display text-[14px] font-bold text-slate-900">{o.active}</p>
                    </td>
                    <td className="px-2 py-3">
                      <p className="font-display text-[14px] font-bold text-slate-900">{o.pipeline_value}</p>
                    </td>
                    <td className="px-2 py-3">
                      <p className={`font-display text-[14px] font-bold ${o.avg_chain >= 75 ? "text-indigo-700" : o.avg_chain >= 50 ? "text-slate-900" : "text-amber-700"}`}>{o.avg_chain}</p>
                    </td>
                    <td className="px-2 py-3">
                      {o.at_risk > 0 ? (
                        <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border bg-rose-50 text-rose-700 border-rose-200">
                          <AlertOctagon size={10} /> {o.at_risk}
                        </span>
                      ) : (
                        <span className="text-[10.5px] text-slate-400 font-mono-cf">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </section>
  );
};
